import { PlantPerformanceFormStep } from '@/types/forms/formSteps/plantPerformanceFormStep';
import { PowerConnectionFormStep } from '@/types/forms/formSteps/powerConnectionFormStep';
import {
    formatGermanNumber,
    parseGermanFloat,
} from '@/utilities/germanFormats';
import { getOverallActivePower } from './plantPerformance';
import { ApplicationType } from '@/types/forms/specific/applicationForm';
import { powerMatchesConnectionRules } from '@/utilities/utils';
import { InvalidGenerationUnit } from '@/types/forms/shared/invalidGenerationUnit';

/**
 * Calculates and sets {@link PlantPerformanceFormStep.feedInPower.value}, if applicable
 * @param applicationType
 * @param plantPerformanceModel
 * @param powerConnectionFormStep
 */
export function calculateFeedInPower(
    applicationType: ApplicationType | undefined,
    plantPerformanceModel: PlantPerformanceFormStep,
    powerConnectionFormStep: PowerConnectionFormStep,
): void {
    const power = getOverallActivePowerWithPreExisting(
        applicationType,
        plantPerformanceModel,
        powerConnectionFormStep,
    );
    const powerText = formatGermanNumber(power, {
        decimals: 2,
        thousandsSeparator: true,
    });

    // Always set installed power
    powerConnectionFormStep.feedInPowerInstalled.value = powerText;

    // Only set limited power, if it's not set to manual input
    if (
        !powerConnectionFormStep.feedInPowerShallBeLimitedByAController
            .disabled &&
        !powerConnectionFormStep.feedInPowerShallBeLimitedByAController.value
    ) {
        powerConnectionFormStep.feedInPower.value = powerText;
    }
}

/**
 * Retrieves combined active power of new and pre existing plants
 * @param applicationType
 * @param plantPerformanceModel step containing value(s) for new plant(s)
 * @param powerConnectionFormStep step containing value for pre-existing plant(s)
 * @returns
 */
export function getOverallActivePowerWithPreExisting(
    applicationType: ApplicationType | undefined,
    plantPerformanceModel: PlantPerformanceFormStep,
    powerConnectionFormStep: PowerConnectionFormStep,
): number {
    const overallActivePower =
        getOverallActivePower(applicationType, plantPerformanceModel) ?? 0;

    const existingPlantsTotalPower = parseGermanFloat(
        !powerConnectionFormStep.existingPlantsTotalPower.disabled
            ? powerConnectionFormStep.existingPlantsTotalPower.value ?? '0'
            : '0',
    );
    const repoweringTotalPower = parseGermanFloat(
        !powerConnectionFormStep.repoweringTotalPower.disabled
            ? powerConnectionFormStep.repoweringTotalPower.value ?? '0'
            : '0',
    );

    return overallActivePower + existingPlantsTotalPower - repoweringTotalPower;
}

/**
 *
 * @param applicationType
 * @param plantPerformanceModel
 * @returns
 */
export function getInvalidGenerationUnits(
    applicationType: ApplicationType | undefined,
    plantPerformanceModel: PlantPerformanceFormStep,
): InvalidGenerationUnit[] {
    return getInvalidGenerationUnitsInternal(
        applicationType,
        plantPerformanceModel,
        getOverallActivePower(applicationType, plantPerformanceModel) ?? 0,
    );
}

/**
 *
 * @param applicationType
 * @param plantPerformanceModel
 * @param powerConnectionFormStep
 * @returns
 */
export function getInvalidGenerationUnitsWithPreExisting(
    applicationType: ApplicationType | undefined,
    plantPerformanceModel: PlantPerformanceFormStep,
    powerConnectionFormStep: PowerConnectionFormStep,
): InvalidGenerationUnit[] {
    return getInvalidGenerationUnitsInternal(
        applicationType,
        plantPerformanceModel,
        getOverallActivePowerWithPreExisting(
            applicationType,
            plantPerformanceModel,
            powerConnectionFormStep,
        ),
    );
}

/**
 *
 * @param applicationType
 * @param plantPerformanceModel
 * @param overallActivePower
 * @returns
 */
function getInvalidGenerationUnitsInternal(
    applicationType: ApplicationType | undefined,
    plantPerformanceModel: PlantPerformanceFormStep,
    overallActivePower: number,
): InvalidGenerationUnit[] {
    const invalidUnits = [] as InvalidGenerationUnit[];

    // Inverters
    for (const module of plantPerformanceModel.inverter.inverterModules.items.concat(
        plantPerformanceModel.inverterStorage.inverterModules.items,
    )) {
        if (module.disabled) {
            continue; // Disabled items shouldn't be processed in the first place
        }

        if (!module.inverterZerezIdAndNorm.zerezId.value) {
            // Filter out automatically created, initial items,
            // as each properly filled entry is expected to have this value set (among others)
            continue;
        }

        const enableManualInput =
            !module.inverterManualInputManufacturerTypeZerezId.disabled &&
            module.inverterManualInputManufacturerTypeZerezId.value === true;
        if (enableManualInput) {
            continue; // Generation units with manual input are not validated
        }

        if (
            !powerMatchesConnectionRules(
                overallActivePower,
                module.inverterZerezIdAndNorm.norm?.value ?? [],
            )
        ) {
            invalidUnits.push({
                manufacturer:
                    module.inverterManufacturerAndModuleType.manufacturer
                        ?.value ?? '',
                moduleType:
                    module.inverterManufacturerAndModuleType.moduleType
                        ?.value ?? '',
                zerezId: module.inverterZerezIdAndNorm.zerezId?.value ?? '',
                norm: module.inverterZerezIdAndNorm.norm?.value ?? [],
            });
        }
    }

    // Generators
    for (const module of plantPerformanceModel.generator.generatorModules
        .items) {
        if (module.disabled) {
            continue; // Disabled items shouldn't be processed in the first place
        }

        if (!module.generatorZerezIdAndNorm.zerezId.value) {
            // Filter out automatically created, initial items,
            // as each properly filled entry is expected to have this value set (among others)
            continue;
        }

        const enableManualInput =
            !module.generatorManualInputManufacturerTypeZerezId.disabled &&
            module.generatorManualInputManufacturerTypeZerezId.value === true;
        if (enableManualInput) {
            continue; // Generation units with manual input are not validated
        }

        if (
            !powerMatchesConnectionRules(
                overallActivePower,
                module.generatorZerezIdAndNorm.norm?.value ?? [],
            )
        ) {
            invalidUnits.push({
                manufacturer:
                    module.generatorManufacturerAndModuleType.manufacturer
                        ?.value ?? '',
                moduleType:
                    module.generatorManufacturerAndModuleType.moduleType
                        ?.value ?? '',
                zerezId: module.generatorZerezIdAndNorm.zerezId?.value ?? '',
                norm: module.generatorZerezIdAndNorm.norm?.value ?? [],
            });
        }
    }

    return invalidUnits;
}
